import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Star, ChevronRight, Sparkles, TrendingUp, Award, Zap, Shield } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import LiveAuctionStrip from "@/components/LiveAuctionStrip";

const PerkRow = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4 items-start p-5 bg-white border border-[#E2E8F0] rounded-xl">
    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
      <Icon size={18} />
    </div>
    <div>
      <h3 className="font-semibold text-[#0F172A] mb-1">{title}</h3>
      <p className="text-sm text-[#64748B] leading-relaxed">{body}</p>
    </div>
  </div>
);

const CompareRow = ({ feature, std, prem }) => (
  <tr className="border-t border-[#E2E8F0]">
    <td className="py-3 px-4 text-sm text-[#0F172A]">{feature}</td>
    <td className="py-3 px-4 text-center text-sm text-[#64748B]">{std}</td>
    <td className="py-3 px-4 text-center text-sm font-semibold text-amber-700 bg-amber-50">{prem}</td>
  </tr>
);

const FaqItem = ({ q, a }) => (
  <details className="group border-b border-[#E2E8F0] py-4">
    <summary className="cursor-pointer list-none flex items-center justify-between font-medium text-[#0F172A]">
      <span>{q}</span>
      <ChevronRight size={16} className="text-[#94A3B8] group-open:rotate-90 transition-transform" />
    </summary>
    <p className="text-sm text-[#64748B] leading-relaxed mt-3">{a}</p>
  </details>
);

const PremiumListingPage = () => {
  const { lang } = useLang();
  const isNl = lang === "nl";
  const navigate = useNavigate();

  React.useEffect(() => {
    document.title = isNl
      ? "Premium listing — krijg meer zichtbaarheid | HorseAppCompare"
      : "Premium listing — get more visibility | HorseAppCompare";
  }, [isNl]);

  const t = isNl ? {
    eyebrow: "Voor app-eigenaren",
    h1: "Premium listing — krijg meer zichtbaarheid voor je paardenapp",
    lede: "Verschijn bovenaan in de gids, krijg een gouden badge en wordt zichtbaar in 22 talen. Geen Stripe-account nodig — wij sturen je een factuur per e-mail.",
    cta1: "Start met Premium",
    cta2: "Eerst gratis aanmelden",
    perksTitle: "Wat krijg je?",
    perks: [
      { icon: Zap, title: "Instant publish", body: "Standaard reviews duren 2-3 werkdagen. Premium gaat live binnen 24 uur na betaling van de factuur." },
      { icon: TrendingUp, title: "Score-boost ×1,4", body: "Onze ranking-formule beloont Premium met een hogere score, dus je ranked automatisch hoger in de directory, Top 100 en alle categoriepagina's." },
      { icon: Award, title: "Gouden Premium-badge", body: "Een onmiskenbaar gouden badge op elke listing-kaart (directory, rankings, vergelijkingen, versus-pagina's en je app-detail)." },
      { icon: Sparkles, title: "TrendingStrip-vermelding", body: "Vast gepind in de 'Trending this week' strook op de homepage, naast de meest bezochte apps." },
      { icon: Shield, title: "Aanwezig in alle 22 talen", body: "Premium-vermelding wordt automatisch vertaald en opgenomen in de SEO-sitemap van alle 22 ondersteunde Europese talen." },
      { icon: Star, title: "Voorrang op support", body: "Direct contact via support@horseappcompare.com voor wijzigingen en updates. Standaardvermeldingen lopen via het reguliere formulier." },
    ],
    compareTitle: "Standaard vs Premium",
    rows: [
      ["Vermelding in de gids", "Ja", "Ja"],
      ["Beschikbaar in 22 talen", "Ja", "Ja"],
      ["Opname in Top 100", "Ja", "Ja"],
      ["Review-tijd voor publicatie", "2-3 werkdagen", "< 24 uur na betaling"],
      ["Score-boost (×1,4)", "—", "✓"],
      ["Gouden Premium-badge", "—", "✓"],
      ["TrendingStrip pin", "—", "✓"],
      ["Voorrang op support", "—", "✓"],
    ],
    pricing: "Eenvoudige prijzen, geen verrassingen",
    monthly: "€99 / maand",
    yearly: "€990 / jaar",
    yearlySub: "2 maanden gratis vs. maandelijks",
    monthlySub: "Per maand opzegbaar",
    faqTitle: "Veelgestelde vragen",
    faqs: [
      ["Hoe werkt de betaling?", "We sturen je een factuur per e-mail binnen 1 werkdag na je aanmelding. Je kunt betalen via bankoverschrijving of iDEAL. Zodra de betaling binnen is, activeren we Premium en gaat je app live."],
      ["Heb je een Stripe-account nodig?", "Nee. We werken bewust met factuur i.p.v. een betaalprovider — sneller voor jou en geen extra account aanmaken. BTW wordt correct verrekend."],
      ["Hoe zit het met BTW?", "Voor Nederlandse bedrijven brengen we 21% BTW in rekening. Voor EU-bedrijven met geldig BTW-nummer hanteren we de verleggingsregeling (0% BTW). Niet-EU: geen BTW."],
      ["Kan ik op elk moment opzeggen?", "Ja. Maandelijks betalend kun je per maand opzeggen — stuur een email naar support@horseappcompare.com vóór de eerstvolgende factuurdatum. Jaarlijks loopt af na 12 maanden, daarna weer per maand opzegbaar."],
      ["Wat als ik niet betaal?", "Geen probleem — je submission blijft in onze admin-wachtrij als 'pending'. We kunnen je app dan handmatig als Standard publiceren (gratis) als je dat wilt."],
      ["Wijzigt mijn ranking als ik upgrade?", "Ja. De ×1,4 boost gaat direct in zodra Premium actief is. Je ranking blijft natuurlijk afhankelijk van de werkelijke feature-set, beoordelingen en gebruikersbasis — Premium versterkt alleen je signaal, het maakt geen middelmatige app naar #1."],
      ["Kan ik mijn vermelding zelf bewerken?", "Stuur een email met de wijzigingen — Premium krijgt voorrang en we passen dit binnen 1 werkdag aan. Self-service editor staat op de roadmap."],
    ],
    ctaFinal: "Klaar om bovenaan te staan?",
    ctaFinalSub: "Vul het formulier in, kies Premium en je app is binnen 24u live na betaling.",
  } : {
    eyebrow: "For app owners",
    h1: "Premium listing — get more visibility for your horse app",
    lede: "Appear at the top of the directory, get a gold badge and be visible in 22 languages. No Stripe account required — we email you an invoice.",
    cta1: "Start with Premium",
    cta2: "List for free first",
    perksTitle: "What you get",
    perks: [
      { icon: Zap, title: "Instant publish", body: "Standard reviews take 2-3 business days. Premium goes live within 24 hours after the invoice is paid." },
      { icon: TrendingUp, title: "Score boost ×1.4", body: "Our ranking formula rewards Premium with a higher score, so you automatically rank higher in the directory, Top 100 and all category pages." },
      { icon: Award, title: "Gold Premium badge", body: "An unmistakable gold badge on every listing card (directory, rankings, comparisons, versus pages and your app detail)." },
      { icon: Sparkles, title: "TrendingStrip placement", body: "Pinned in the 'Trending this week' strip on the homepage, next to the most visited apps." },
      { icon: Shield, title: "Available in all 22 languages", body: "Premium listings are automatically translated and included in the SEO sitemap for all 22 supported European languages." },
      { icon: Star, title: "Priority support", body: "Direct line via support@horseappcompare.com for edits and updates. Standard listings go through the regular form." },
    ],
    compareTitle: "Standard vs Premium",
    rows: [
      ["Directory listing", "Yes", "Yes"],
      ["Available in 22 languages", "Yes", "Yes"],
      ["Eligible for Top 100", "Yes", "Yes"],
      ["Review time before publishing", "2-3 business days", "< 24h after payment"],
      ["Score boost (×1.4)", "—", "✓"],
      ["Gold Premium badge", "—", "✓"],
      ["TrendingStrip pin", "—", "✓"],
      ["Priority support", "—", "✓"],
    ],
    pricing: "Simple pricing, no surprises",
    monthly: "€99 / month",
    yearly: "€990 / year",
    yearlySub: "2 months free vs. monthly",
    monthlySub: "Cancel any month",
    faqTitle: "Frequently asked questions",
    faqs: [
      ["How does payment work?", "We email you an invoice within 1 business day of your submission. You can pay via bank transfer or iDEAL. Once received, we activate Premium and your app goes live."],
      ["Do I need a Stripe account?", "No. We intentionally use invoices instead of a payment processor — faster for you, no extra accounts. VAT is handled correctly."],
      ["What about VAT?", "For Dutch companies we charge 21% VAT. For EU companies with a valid VAT number we apply the reverse-charge mechanism (0% VAT). Non-EU: no VAT."],
      ["Can I cancel anytime?", "Yes. Monthly plans cancel per month — email support@horseappcompare.com before the next invoice date. Yearly auto-expires after 12 months, then becomes month-to-month."],
      ["What if I don't pay?", "No problem — your submission stays in our admin queue as 'pending'. We can publish it as Standard (free) on request."],
      ["Does my ranking change when I upgrade?", "Yes. The ×1.4 boost kicks in immediately once Premium is active. Your ranking still depends on real feature set, ratings and user base — Premium amplifies your signal, it won't push a mediocre app to #1."],
      ["Can I edit my listing myself?", "Email the changes — Premium gets priority and we update within 1 business day. Self-service editor is on the roadmap."],
    ],
    ctaFinal: "Ready to be on top?",
    ctaFinalSub: "Fill in the form, choose Premium and your app is live within 24h after payment.",
  };

  return (
    <main data-testid="premium-listing-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 via-white to-white border-b border-[#E2E8F0]">
        <div className="hac-container py-20 max-w-4xl">
          <div className="overline mb-3 text-amber-600 inline-flex items-center gap-1.5">
            <Star size={12} className="fill-current" /> {t.eyebrow}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-5">
            {t.h1}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl mb-8">{t.lede}</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => navigate("/submit-app?tier=premium_monthly")} className="hac-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500" data-testid="premium-cta-primary">
              <Star size={14} className="fill-current" /> {t.cta1}
            </button>
            <Link to="/submit-app" className="hac-btn-secondary" data-testid="premium-cta-secondary">
              {t.cta2}
            </Link>
          </div>
        </div>
      </section>

      {/* Live preview side-by-side */}
      <section className="hac-container py-16 max-w-5xl">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Standard card */}
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 relative">
            <div className="absolute -top-3 left-6 bg-[#F1F5F9] text-[#64748B] text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">{isNl ? "Standaard" : "Standard"}</div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-[#2563EB] text-white flex items-center justify-center font-heading text-lg">E</div>
              <div>
                <div className="font-heading text-xl text-[#0F172A]">EquineFit Pro</div>
                <div className="text-xs text-[#94A3B8]">Netherlands</div>
              </div>
            </div>
            <p className="text-sm text-[#64748B] mb-3">{isNl ? "AI-trainingsplanner voor dressuur en springen." : "AI training planner for dressage and jumping."}</p>
            <div className="text-xs text-[#94A3B8]">{isNl ? "Score" : "Score"}: <span className="font-mono">62</span></div>
          </div>
          {/* Premium card */}
          <div className="bg-white border border-amber-300 rounded-2xl p-6 relative shadow-md ring-1 ring-amber-200">
            <div className="absolute -top-3 left-6 bg-amber-400 text-amber-900 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded inline-flex items-center gap-1"><Star size={9} className="fill-current" /> Premium</div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-sm">
              <Star size={10} className="fill-current" /> Premium
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-lg bg-[#2563EB] text-white flex items-center justify-center font-heading text-lg">E</div>
              <div>
                <div className="font-heading text-xl text-[#0F172A]">EquineFit Pro</div>
                <div className="text-xs text-[#94A3B8]">Netherlands</div>
              </div>
            </div>
            <p className="text-sm text-[#64748B] mb-3">{isNl ? "AI-trainingsplanner voor dressuur en springen." : "AI training planner for dressage and jumping."}</p>
            <div className="text-xs text-amber-700 font-medium">{isNl ? "Score" : "Score"} (×1.4 boost): <span className="font-mono">87</span> ↗</div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="hac-container py-12 max-w-5xl">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-8">{t.perksTitle}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {t.perks.map((p, i) => <PerkRow key={i} icon={p.icon} title={p.title} body={p.body} />)}
        </div>
      </section>

      {/* Comparison table */}
      <section className="hac-container py-12 max-w-4xl">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-8">{t.compareTitle}</h2>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F8FAFC]">
              <tr>
                <th className="py-3 px-4 text-left text-xs uppercase tracking-widest text-[#64748B]"></th>
                <th className="py-3 px-4 text-center text-xs uppercase tracking-widest text-[#64748B]">{isNl ? "Standaard" : "Standard"}</th>
                <th className="py-3 px-4 text-center text-xs uppercase tracking-widest text-amber-700 bg-amber-50">Premium</th>
              </tr>
            </thead>
            <tbody>
              {t.rows.map(([f, s, p], i) => <CompareRow key={i} feature={f} std={s} prem={p} />)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Pricing */}
      <section className="hac-container py-16 max-w-4xl">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-2">{t.pricing}</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-8">
          <div className="bg-white border-2 border-amber-200 rounded-2xl p-7 text-center">
            <div className="text-sm text-[#64748B] mb-2">{isNl ? "Per maand" : "Monthly"}</div>
            <div className="text-4xl font-bold text-[#0F172A] mb-1">{t.monthly}</div>
            <div className="text-xs text-[#94A3B8] mb-5">{t.monthlySub}</div>
            <button onClick={() => navigate("/submit-app?tier=premium_monthly")} className="hac-btn-secondary w-full" data-testid="premium-pricing-monthly">{t.cta1}</button>
          </div>
          <div className="bg-amber-50 border-2 border-amber-400 rounded-2xl p-7 text-center relative">
            <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full">{isNl ? "Beste deal" : "Best value"}</span>
            <div className="text-sm text-amber-700 mb-2">{isNl ? "Per jaar" : "Yearly"}</div>
            <div className="text-4xl font-bold text-[#0F172A] mb-1">{t.yearly}</div>
            <div className="text-xs text-amber-700 font-medium mb-5">{t.yearlySub}</div>
            <button onClick={() => navigate("/submit-app?tier=premium_yearly")} className="hac-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500 w-full" data-testid="premium-pricing-yearly"><Star size={14} className="fill-current" /> {t.cta1}</button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="hac-container py-12 max-w-3xl">
        <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-8">{t.faqTitle}</h2>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
          {t.faqs.map(([q, a], i) => <FaqItem key={i} q={q} a={a} />)}
        </div>
      </section>

      {/* Media Kit download */}
      <LiveAuctionStrip />

      {/* Media Kit download */}
      <section className="hac-container py-8 max-w-3xl">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/media-kit.pdf`} target="_blank" rel="noreferrer" className="block bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-2xl p-6 transition-colors group" data-testid="premium-mediakit-download">
          <div className="flex items-center gap-5">
            <div className="text-5xl shrink-0">📄</div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-1">{lang === "nl" ? "Voor designers" : "For designers"}</div>
              <div className="font-bold text-lg mb-1">{lang === "nl" ? "Download de Media Kit (PDF)" : "Download the Media Kit (PDF)"}</div>
              <p className="text-sm text-white/70">{lang === "nl"
                ? "Alle advertentie-afmetingen, formaten, safe-zones en wireframes — 5 pagina's, klaar voor je designteam."
                : "All ad dimensions, formats, safe zones and wireframes — 5 pages, ready for your design team."}</p>
            </div>
            <ChevronRight size={20} className="text-amber-300 group-hover:translate-x-1 transition-transform shrink-0" />
          </div>
        </a>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
        <div className="hac-container py-16 max-w-3xl text-center">
          <Star size={32} className="mx-auto mb-4 fill-current" />
          <h2 className="font-heading text-3xl md:text-5xl mb-3">{t.ctaFinal}</h2>
          <p className="text-amber-50 mb-6">{t.ctaFinalSub}</p>
          <button onClick={() => navigate("/submit-app?tier=premium_monthly")} className="bg-white text-amber-700 hover:bg-amber-50 font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="premium-cta-final">
            {t.cta1} <ChevronRight size={16} />
          </button>
        </div>
      </section>
    </main>
  );
};

export default PremiumListingPage;
